import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "antd";
import {
  CloseOutlined,
  DeleteOutlined,
  FolderOpenOutlined,
  WarningOutlined,
  DownloadOutlined,
  DownOutlined,
  EditOutlined,
  PlusOutlined,
  RightOutlined,
  UploadOutlined,
  SbSelect,
  SbSwitch,
  SbTag,
  SbTooltip,
} from "uiCore";
import SbModal from "uiCore/SbModal/SbModal";
import SbInfoToaster from "Components/SbErrorToasterComponent/SbInfoToaster";
import SbButton from "uiCore/SbButton/SbButton";
import SbFilePill from "uiCore/SbFilePill/SbFilePill";
import SbUpload from "uiCore/SbUpload/SbUpload";
import SbInput from "uiCore/SbInput/SbInput";
import useAuthStore from "Store/useAuthStore";
import { useOptions, expandSentinels, joinMulti } from "Components/SbJdCreationDrawerV2/helpers";
import { buildFieldConfigMap } from "Utils/formatting";
import { showSbConfirm } from "Utils/sbModal";
import { validateExcelFile, validateUploadedFile } from "Utils/fileValidation";
import { useDispatch } from "react-redux";
import { downloadBulkJdTemplate, submitBulkJdUpload, getRoleReportingView } from "Api/apiService";
import { showToast } from "Store/actions/profileListActions";
import * as XLSX from "xlsx";
import useZipMatcher from "./useZipMatcher";
import SbZipMappingReview from "./SbZipMappingReview";
import {
  BATCH_LIMIT, EMPTY_ROW, CASCADE_RESETS,
  getCascade, getMissingFields, hasRealSpecialties, resolveSpecialty, parseTemplateRows,
} from "./bulkJdUpload.utils";

import {
  PageContainerStyled,
  PageHeaderStyled,
  HeaderLeftStyled,
  HeaderRightStyled,
  HeadingRowStyled,
} from "styles/common.styles";

import {
  PremiumButtonWrapper,
  BulkEmbedContainerStyled,
  ToolbarRowStyled,
  ToolbarActionsStyled,
  CardSectionDividerStyled,
  FileUploadGroupStyled,
  FilePillStyled,
  RowListStyled,
  RowCardStyled,
  RowCardHeaderStyled,
  RowCardHeaderLeftStyled,
  RowCardHeaderRightStyled,
  RowCardBodyStyled,
  RowCardSummaryStyled,
  SummaryItemStyled,
  SummaryLabelStyled,
  SummaryValueStyled,
  SummaryPipeStyled,
  FieldsRowStyled,
  FieldItemStyled,
  FieldLabelStyled,
  RequiredMark,
  RowMissingBadge,
  FieldValueStyled,
  FieldValueReadOnlyStyled,
  EmptyStateStyled,
  SubmitRowStyled,
  RoleContextValueBoxStyled,
  RowHintStyled,
  StepBarStyled,
  StepBadgeStyled,
  StepLabelStyled,
  StepDividerStyled,
  StepHintStyled,
  StepBarLeftStyled,
  StepBarRightStyled,
  StepClickableGroupStyled,
} from "./SbBulkJdUpload.styles";

const { Title, Text } = Typography;

const TOOLTIP_ATTACH_JD = "Attach the JD document (PDF or DOCX) for this specific role. Required before submitting.";
const TOOLTIP_DOWNLOAD_TEMPLATE = "Download the pre-formatted Excel template for bulk JD upload. The template includes dropdown validation pre-populated from your org data.";
const TOOLTIP_DROP_ZONE = "Drop your completed Excel template here or click to browse. Maximum 50 rows per batch, .xlsx format only.";
const TOOLTIP_SUBMIT_BATCH = "Submit all rows in the review table for processing. Each row will be validated and the JD generation will begin.";

const previewFile = (file) => {
  const url = URL.createObjectURL(file);
  if (file.type === 'application/pdf') {
    window.open(url, '_blank');
    URL.revokeObjectURL(url);
  } else {
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(url);
  }
};

const SbBulkJdUpload = ({ embedded = false, onDirtyChange, onSubmitSuccess }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const orgMetaData = useAuthStore((s) => s.orgMetaData);
  const email = useAuthStore((s) => s.email);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const orgStructure = useMemo(() => orgMetaData?.organization_structure || [], [orgMetaData]);
  const orgStructureWrapped = useMemo(() => ({ organization_structure: orgStructure }), [orgStructure]);

  const [fileList, setFileList] = useState([]);
  const [rows, setRows] = useState([]);
  const [roleContextModal, setRoleContextModal] = useState(null);
  const [roleContextInput, setRoleContextInput] = useState(null);
  const [collapsedKeys, setCollapsedKeys] = useState(new Set());
  const [highlightedRowKey, setHighlightedRowKey] = useState(null);
  const [invalidatedKeys, setInvalidatedKeys] = useState(new Set());
  const [editableKeys, setEditableKeys] = useState(new Set());
  const pendingScrollKey = useRef(null);
  const reviewRef = useRef(null);

  const { hasZip, zipError, zipFilenames, loadZip, clearZip, computeMatches, resolveFiles } = useZipMatcher();
  const [zipFilename, setZipFilename] = useState(null);
  const [zipMatches, setZipMatches] = useState(null); // { autoMapped, needsReview } | null
  const [mappingsConfirmed, setMappingsConfirmed] = useState(false);
  const [step, setStep] = useState(1);
  const [zipPendingCount, setZipPendingCount] = useState(0);
  const [confirmToaster, setConfirmToaster] = useState(null); // null | { decisions }

  const toggleCollapse = (key) =>
    setCollapsedKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const collapseAll = () => setCollapsedKeys(new Set(rows.map(r => r.key)));
  const expandAll = () => setCollapsedKeys(new Set());

  const isDirty = rows.length > 0 || fileList.length > 0;


  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const jobOptions = useOptions('jobs', null, orgMetaData);
  const legalEntities = useOptions('legal_entities', null, orgStructureWrapped);
  const roleTypeOptions = useOptions('company_structure', 'role_type', orgMetaData);
  const jobLevelOptions = useOptions('company_structure', 'job_grade', orgMetaData);
  const workEnvironmentOptions = useOptions('company_structure', 'Work_Environment', orgMetaData);

  // Shared across every row — same contract as the single JD drawer and the
  // Admin Panel Role Reporting feature: manager is identified by sb_unique_id,
  // not job_id/job_code (a revised role keeps its job_id but gets a new
  // sb_unique_id, and that's the column the backend resolves against).
  const [roleReportingToOptions, setRoleReportingToOptions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rowsResp = await getRoleReportingView(email);
        if (cancelled) return;
        setRoleReportingToOptions(
          (Array.isArray(rowsResp) ? rowsResp : [])
            .filter((r) => r?.sb_unique_id)
            .map((r) => ({
              value: r.sb_unique_id,
              label: r.job_code ? `${r.job_title} (${r.job_code})` : r.job_title || r.sb_unique_id,
            }))
        );
      } catch {
        if (!cancelled) setRoleReportingToOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [email]);

  const fieldConfigMap = useMemo(() => buildFieldConfigMap(orgMetaData, 'create_jd'), [orgMetaData]);
  const fields = useMemo(() => ({
    jobCode:         fieldConfigMap['job_code']         || {},
    roleTitle:       fieldConfigMap['role']             || {},
    altRoleTitle:    fieldConfigMap['alt_role_title']   || {},
    legalEntity:     fieldConfigMap['legal_entity']     || {},
    businessUnit:    fieldConfigMap['business_unit']    || {},
    function:        fieldConfigMap['function']         || {},
    subFunction:     fieldConfigMap['sub_function']     || {},
    specialty:       fieldConfigMap['specialty']        || {},
    roleType:        fieldConfigMap['role_type']        || {},
    level:           fieldConfigMap['level']            || {},
    workEnvironment: fieldConfigMap['work_environment'] || {},
    location:        fieldConfigMap['location']         || {},
    roleContext:     fieldConfigMap['role_context']     || {},
  }), [fieldConfigMap]);

  // Required fields derived from metadata — label from org, visible=false excludes from validation
  const requiredFields = useMemo(() => [
    fields.roleTitle?.visible !== false && { key: 'jobTitle',       label: fields.roleTitle?.label       || 'Job Title' },
    fields.function?.visible  !== false && { key: 'functionDept',   label: fields.function?.label        || 'Function' },
    fields.subFunction?.visible !== false && { key: 'subFunction',  label: fields.subFunction?.label     || 'Sub-function' },
    fields.specialty?.visible && fields.specialty?.required && { key: 'specialty', label: fields.specialty?.label || 'Specialty' },
    fields.workEnvironment?.visible !== false && { key: 'workEnvironment', label: fields.workEnvironment?.label || 'Work Environment' },
    fields.location?.visible !== false && { key: 'location',        label: fields.location?.label        || 'Location' },
  ].filter(Boolean), [fields]);

  const requiredKeys = useMemo(() => new Set(requiredFields.map(f => f.key)), [requiredFields]);

  const updateRow = (key, changes) => {
    if (highlightedRowKey === key) setHighlightedRowKey(null);
    setRows(prev => prev.map(r => r.key === key ? { ...r, ...changes } : r));
  };

  const isRowBlank = (row) =>
    !row.jobCode && !row.standardIndustryName && !row.jobTitle &&
    !row.legalEntity && !row.businessUnit && !row.functionDept &&
    !row.subFunction && !row.specialty?.length && !row.roleType && !row.jobLevel &&
    !row.workEnvironment && !row.location && !row.file && !row.roleContext &&
    !row.roleReportingToSbUniqueId && !row.isApexRole;

  const addRow = () => {
    if (rows.length >= BATCH_LIMIT) return;
    const lastManual = [...rows].reverse().find(r => r.source === 'manual');
    if (lastManual && isRowBlank(lastManual)) {
      setHighlightedRowKey(lastManual.key);
      setCollapsedKeys(prev => { const next = new Set(prev); next.delete(lastManual.key); return next; });
      return;
    }
    const newKey = `manual-${Date.now()}`;
    pendingScrollKey.current = newKey;
    setRows(prev => [...prev, { ...EMPTY_ROW, key: newKey }]);
  };

  const handleRemoveAll = () => showSbConfirm({
    title: 'Remove all rows',
    content: fileList.length > 0
      ? 'This will discard the uploaded Excel file and all rows loaded from it. This cannot be undone.'
      : 'This will discard all rows and cannot be undone.',
    okText: 'Remove All',
    cancelText: 'Cancel',
    onOk: () => { setRows([]); setFileList([]); setInvalidatedKeys(new Set()); setEditableKeys(new Set()); clearZip(); setZipFilename(null); setZipMatches(null); setMappingsConfirmed(false); setZipPendingCount(0); setStep(1); },
  });

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await downloadBulkJdTemplate(email);
      const url = URL.createObjectURL(response.data);
      const disposition = response.headers['content-disposition'];
      const filename = disposition
        ? disposition.split('filename=').pop().replace(/"/g, '').trim()
        : 'bulk_jd_template.xlsx';
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      dispatch(showToast('Failed to download template. Please try again.', 'error'));
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleDiscardFile = () => {
    showSbConfirm({
      title: 'Discard uploaded file?',
      content: 'Removing this file will also discard all rows loaded from it. This cannot be undone.',
      okText: 'Discard File & Rows',
      cancelText: 'Cancel',
      onOk: () => {
        setFileList([]);
        setRows(prev => prev.filter(r => r.source !== 'excel'));
        setEditableKeys(prev => {
          const next = new Set(prev);
          // remove keys that belonged to excel rows (they're being discarded)
          rows.filter(r => r.source === 'excel').forEach(r => next.delete(r.key));
          return next;
        });
      },
    });
  };

  const handleZipUpload = async (file) => {
    setZipFilename(file.name);
    setMappingsConfirmed(false);
    await loadZip(file);
  };

  const handleZipRemove = () => {
    clearZip();
    setZipFilename(null);
    setZipMatches(null);
    setMappingsConfirmed(false);
    setZipPendingCount(0);
    setRows((prev) => prev.map((r) => ({ ...r, file: null, fileError: null })));
  };

  const handleZipRemoveWithConfirm = () => showSbConfirm({
    title: 'Remove ZIP file?',
    content: 'This will remove the uploaded ZIP and clear all file mappings.',
    okText: 'Remove',
    cancelText: 'Cancel',
    onOk: handleZipRemove,
  });

  const applyMappings = async (decisions) => {
    const zipDecisions = {};
    const directRows   = [];
    Object.entries(decisions).forEach(([rowKey, value]) => {
      if (value instanceof File) directRows.push({ rowKey, file: value });
      else zipDecisions[rowKey] = value;
    });
    let updated = await resolveFiles(rows, zipDecisions);
    directRows.forEach(({ rowKey, file }) => {
      updated = updated.map((r) => r.key === rowKey ? { ...r, file, fileError: null } : r);
    });
    return updated;
  };

  const handleConfirmMappings = async (decisions) => {
    const updated = await applyMappings(decisions);
    setRows(updated);
    setMappingsConfirmed(true);
    dispatch(showToast('File mappings confirmed.', 'success'));
  };

  // Recompute matches whenever zip filenames or rows change and zip is loaded
  useEffect(() => {
    if (!hasZip || !rows.length) { setZipMatches(null); return; }
    setZipMatches(computeMatches(rows));
    setMappingsConfirmed(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasZip, zipFilenames, rows.length]);

  // Splits "<number> - <name>" so levels can be matched by number, not text —
  // avoids padding mismatches like "01 - CXO" vs "1 - CXO".
  const parseLevelText = (text) => {
    const trimmed = String(text ?? '').trim();
    const m = trimmed.match(/^(\d+)\s*-\s*(.+)$/);
    return m ? { number: parseInt(m[1], 10), name: m[2].trim() } : { number: null, name: trimmed };
  };

  const resolveJobLevel = (rawVal) => {
    if (!rawVal) return null;
    const input = parseLevelText(rawVal);

    const match = jobLevelOptions.find(o => {
      const opt = parseLevelText(o.label);
      if (input.number !== null && opt.number !== null) {
        return input.number === opt.number;
      }
      return opt.name.toLowerCase() === input.name.toLowerCase()
        || String(o.value ?? '').toLowerCase() === input.name.toLowerCase();
    });

    // Bare value, not the label — the SbSelect below needs it to match option.value.
    return match ? match.value : String(rawVal).trim();
  };

  // Reattaches the number before submitting, since resolveJobLevel keeps it bare.
  const resolveJobLevelLabel = (jobLevel) =>
    jobLevelOptions.find(o => o.value === jobLevel)?.label || jobLevel || '';

  const parseExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'array' });
        const parsed = parseTemplateRows(workbook, { orgStructure, resolveJobLevel });
        if (!parsed) {
          dispatch(showToast('This template is outdated. Please download the latest template and try again.', 'error'));
          return;
        }

        if (parsed.length === 0) {
          dispatch(showToast('No data rows found in the uploaded Excel file.', 'error'));
        } else {
          setRows(prev => [...prev, ...parsed]);
          // Unlock rows with an invalid Specialty so the user can re-pick it
          const invalidSpecialtyKeys = parsed.filter(r => r.invalidSpecialty).map(r => r.key);
          if (invalidSpecialtyKeys.length) setEditableKeys(prev => new Set([...prev, ...invalidSpecialtyKeys]));
          dispatch(showToast(`Loaded ${parsed.length} row${parsed.length !== 1 ? 's' : ''} from Excel.`, 'success'));
        }
      } catch {
        dispatch(showToast('Failed to read the Excel file. Please ensure it is a valid .xls or .xlsx file.', 'error'));
      }
    };
    reader.onerror = () => {
      dispatch(showToast('Failed to read the Excel file.', 'error'));
      setFileList([]);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSubmitAll = () => {
    const effectiveRows = rows;
    const invalidRows = effectiveRows.filter((r) => getMissingFields(r, requiredFields, orgStructure).length > 0);

    if (invalidRows.length > 0) {
      setInvalidatedKeys(new Set(invalidRows.map((r) => r.key)));
      setCollapsedKeys((prev) => {
        const next = new Set(prev);
        invalidRows.forEach((r) => next.delete(r.key));
        return next;
      });
      // Unlock excel rows that have missing fields so the user can edit only those fields
      const excelInvalidKeys = invalidRows.filter((r) => r.source === 'excel').map((r) => r.key);
      if (excelInvalidKeys.length > 0) {
        setEditableKeys((prev) => new Set([...prev, ...excelInvalidKeys]));
      }
      pendingScrollKey.current = invalidRows[0].key;
      return;
    }

    const rowsToSubmit = effectiveRows.slice(0, BATCH_LIMIT);
    const formData = new FormData();
    formData.append('email', email);

    const metadata = rowsToSubmit.map((r, idx) => ({
      row_index: idx,
      job_code: r.jobCode || '',
      role: r.jobTitle || '',
      alt_role_title: r.standardIndustryName || '',
      legal_entity: r.legalEntity || '',
      business_unit: r.businessUnit || '',
      function: r.functionDept || '',
      sub_function: r.subFunction || '',
      specialty: joinMulti(r.specialty),
      role_type: r.roleType || '',
      level: resolveJobLevelLabel(r.jobLevel),
      work_environment: r.workEnvironment || '',
      location: r.location || '',
      role_context: r.roleContext || '',
      // Mutually exclusive — Is Apex takes precedence if both were somehow
      // set, since an apex role has no one to report to. Matches the Admin
      // Panel Role Reporting / single JD drawer contract: sb_unique_id, not
      // job_id/job_code.
      ...(r.isApexRole
        ? { is_apex_role: true }
        : r.roleReportingToSbUniqueId
          ? { role_reporting_to_sb_unique_id: r.roleReportingToSbUniqueId }
          : {}),
    }));
    formData.append('metadata', JSON.stringify(metadata));

    rowsToSubmit.forEach((r, idx) => {
      if (r.file) formData.append(`file_${idx}`, r.file);
    });

    submitBulkJdUpload(formData).catch((error) => {
      console.error('[BulkJdUpload] Background submission failed:', error);
    });
    setRows([]);
    setFileList([]);
    setInvalidatedKeys(new Set());
    setEditableKeys(new Set());
    clearZip();
    setZipFilename(null);
    setZipMatches(null);
    setMappingsConfirmed(false);
    setZipPendingCount(0);
    setStep(1);
    dispatch(showToast('Processing in background — results will be visible in Jobs Library in some time.', 'success'));
    onSubmitSuccess?.();
  };

  const handleJobCodeChange = (v, rowKey) => {
    if (!v) {
      updateRow(rowKey, {
        jobCode: null, standardIndustryName: '',
        legalEntity: null, businessUnit: null,
        subFunction: null, specialty: [], invalidSpecialty: null, roleType: null, jobLevel: null,
        functionDept: null, location: null,
      });
      return;
    }
    const job = jobOptions.find(o => o.value === v)?.allFields;
    // Prefill the job code's default specialty, same as the JD creation drawer
    const { specialties } = getCascade(orgStructure, job?.legal_entity, job?.business_unit, job?.function, job?.sub_function);
    updateRow(rowKey, {
      jobCode: v,
      standardIndustryName: job?.job_title || '',
      legalEntity: job?.legal_entity || null,
      businessUnit: job?.business_unit || null,
      functionDept: job?.function || null,
      subFunction: job?.sub_function || null,
      specialty: resolveSpecialty(job?.specialty, specialties),
      invalidSpecialty: null,
      roleType: job?.role_type || null,
      jobLevel: resolveJobLevel(job?.level),
      location: null,
    });
  };

  const renderFileSection = (record) => {
    if (record.source === 'excel') return null;
    if (record.file) {
      const status = record.fileError ? 'invalid' : 'valid';
      const tooltipTitle = record.fileError || `${record.file.name} — click to preview`;
      return (
        <FileUploadGroupStyled>
          <SbTooltip title={tooltipTitle}>
            <FilePillStyled
              $status={status}
              onClick={() => !record.fileError && previewFile(record.file)}
            >
              {record.file.name}
            </FilePillStyled>
          </SbTooltip>
          <SbButton
            buttonType="action"
            size="small"
            icon={<CloseOutlined />}
            onClick={() => updateRow(record.key, { file: null, fileError: null })}
          />
        </FileUploadGroupStyled>
      );
    }
    return (
      <FileUploadGroupStyled>
        <SbTooltip content={TOOLTIP_ATTACH_JD}>
          <span>
            <SbUpload
              accept=".pdf,.docx"
              maxCount={1}
              showUploadList={false}
              beforeUpload={async (file) => {
                const error = await validateUploadedFile(file);
                updateRow(record.key, { file, fileError: error || null });
                return false;
              }}
            >
              <PremiumButtonWrapper>
                <SbButton buttonType="secondary" size="small" icon={<UploadOutlined />}>
                  Upload Reference JD
                </SbButton>
              </PremiumButtonWrapper>
            </SbUpload>
          </span>
        </SbTooltip>
      </FileUploadGroupStyled>
    );
  };

  const renderRowCard = (record, idx) => {
    const isExcel = record.source === 'excel';
    // Excel rows that were unlocked for editing due to missing required fields
    const isEditableExcel = isExcel && editableKeys.has(record.key);
    // Returns true when this field should be editable on an excel row (row was unlocked due to missing fields)
    const excelFieldEditable = () => isEditableExcel;
    // Fields locked by job-code auto-fill (same behaviour as External JD upload)
    const isJobCodeLocked = isExcel || !!record.jobCode;
    const { businessUnits, functions, subFunctions, specialties, locations } =
      getCascade(orgStructure, record.legalEntity, record.businessUnit, record.functionDept, record.subFunction);

    const jobCodeLabel = jobOptions.find(o => o.value === record.jobCode)?.label || record.jobCode || '—';
    const jobLevelLabel = jobLevelOptions.find(o => o.value === record.jobLevel)?.label || record.jobLevel || '—';

    const val = (v) => v || '—';
    const isCollapsed = collapsedKeys.has(record.key);
    const missingFields = getMissingFields(record, requiredFields, orgStructure);

    const scrollRefCallback = (el) => {
      if (el && pendingScrollKey.current === record.key) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        pendingScrollKey.current = null;
      }
    };

    const isInvalid = invalidatedKeys.has(record.key) && missingFields.length > 0;

    return (
      <RowCardStyled key={record.key} ref={scrollRefCallback} $highlighted={highlightedRowKey === record.key} $invalid={isInvalid}>
        <RowCardHeaderStyled $collapsed={isCollapsed}>
          <RowCardHeaderLeftStyled onClick={() => toggleCollapse(record.key)} style={{ flex: 'none' }}>
            <SbButton
              buttonType="action"
              size="small"
              icon={isCollapsed ? <RightOutlined /> : <DownOutlined />}
            />
            <Text type="secondary">#{idx + 1}</Text>
            <SbTag color={isExcel ? 'blue' : 'default'}>{isExcel ? 'From Excel' : 'Manual'}</SbTag>
            {isCollapsed && (
              <RowCardSummaryStyled>
                {record.jobCode && (
                  <SummaryItemStyled>
                    <SummaryLabelStyled>{fields.jobCode?.label || 'Job Code & Family'}</SummaryLabelStyled>
                    <SummaryValueStyled>{jobCodeLabel}</SummaryValueStyled>
                  </SummaryItemStyled>
                )}
                {record.jobCode && record.jobTitle && <SummaryPipeStyled>|</SummaryPipeStyled>}
                {record.jobTitle && (
                  <SummaryItemStyled>
                    <SummaryLabelStyled>{fields.roleTitle?.label || 'Job Title'}</SummaryLabelStyled>
                    <SummaryValueStyled>{record.jobTitle}</SummaryValueStyled>
                  </SummaryItemStyled>
                )}
                {!record.jobCode && !record.jobTitle && (
                  <SummaryItemStyled>
                    <SummaryValueStyled>—</SummaryValueStyled>
                  </SummaryItemStyled>
                )}
              </RowCardSummaryStyled>
            )}
          </RowCardHeaderLeftStyled>
          {highlightedRowKey === record.key && (
            <RowHintStyled>
              <WarningOutlined />
              Empty Row, Fill in the details.
            </RowHintStyled>
          )}
          {record.invalidSpecialty && (
            <RowHintStyled>
              <WarningOutlined />
              {`${fields.specialty?.label || 'Specialty'} '${record.invalidSpecialty}' is not valid, please select again.`}
            </RowHintStyled>
          )}
          {isInvalid && (
            <RowMissingBadge>
              <WarningOutlined />
              {isCollapsed
                ? `Missing: ${missingFields.join(', ')}`
                : `${missingFields.length} required field${missingFields.length > 1 ? 's' : ''} missing`}
            </RowMissingBadge>
          )}
          <RowCardHeaderRightStyled>
            {renderFileSection(record)}
            <CardSectionDividerStyled />
            <SbTooltip title="Remove row">
              <span>
                <SbButton
                  buttonType="action"
                  size="small"
                  icon={<DeleteOutlined />}
                  disabled={submitting || mappingsConfirmed}
                  onClick={() => showSbConfirm({
                    title: 'Remove row',
                    content: 'This action cannot be undone.',
                    okText: 'Remove',
                    cancelText: 'Cancel',
                    onOk: () => {
                      setRows(prev => prev.filter(r => r.key !== record.key));
                      setInvalidatedKeys(prev => { const next = new Set(prev); next.delete(record.key); return next; });
                    },
                  })}
                />
              </span>
            </SbTooltip>
          </RowCardHeaderRightStyled>
        </RowCardHeaderStyled>

        {!isCollapsed && <RowCardBodyStyled>
          {/* Field row 1 */}
          <FieldsRowStyled>
            {fields.jobCode?.visible !== false && (
              <FieldItemStyled>
                <FieldLabelStyled>{fields.jobCode?.label || 'Job Code & Family'}</FieldLabelStyled>
                {isExcel ? (
                  <FieldValueReadOnlyStyled>{jobCodeLabel}</FieldValueReadOnlyStyled>
                ) : (
                  <SbSelect options={jobOptions} value={record.jobCode} placeholder="Select"
                    allowClear showSearch size="small"
                    onChange={(v) => handleJobCodeChange(v, record.key)} />
                )}
              </FieldItemStyled>
            )}
            {fields.altRoleTitle?.visible !== false && (
              <FieldItemStyled>
                <FieldLabelStyled>{fields.altRoleTitle?.label || 'Std. Industry Name'}</FieldLabelStyled>
                {isJobCodeLocked ? (
                  <FieldValueReadOnlyStyled>{val(record.standardIndustryName)}</FieldValueReadOnlyStyled>
                ) : (
                  <SbInput value={record.standardIndustryName} placeholder="Enter name" size="small"
                    onValueChange={(v) => updateRow(record.key, { standardIndustryName: v })} />
                )}
              </FieldItemStyled>
            )}
            {fields.roleTitle?.visible !== false && (
              <FieldItemStyled>
                <FieldLabelStyled>
                  {fields.roleTitle?.label || 'Job Title'}
                  {requiredKeys.has('jobTitle') && <RequiredMark>*</RequiredMark>}
                </FieldLabelStyled>
                {isExcel && !excelFieldEditable('jobTitle') ? (
                  <FieldValueReadOnlyStyled>{val(record.jobTitle)}</FieldValueReadOnlyStyled>
                ) : (
                  <SbInput value={record.jobTitle} placeholder="Enter title" size="small"
                    onValueChange={(v) => updateRow(record.key, { jobTitle: v })} />
                )}
              </FieldItemStyled>
            )}
            {fields.legalEntity?.visible !== false && (
              <FieldItemStyled>
                <FieldLabelStyled>{fields.legalEntity?.label || 'Legal Entity'}</FieldLabelStyled>
                {isJobCodeLocked ? (
                  <FieldValueReadOnlyStyled>{val(record.legalEntity)}</FieldValueReadOnlyStyled>
                ) : (
                  <SbSelect options={legalEntities} value={record.legalEntity} placeholder="Select"
                    allowClear size="small"
                    onChange={(v) => updateRow(record.key, { legalEntity: v, ...CASCADE_RESETS.legalEntity })} />
                )}
              </FieldItemStyled>
            )}
            {fields.businessUnit?.visible !== false && (
              <FieldItemStyled>
                <FieldLabelStyled>{fields.businessUnit?.label || 'Business Unit'}</FieldLabelStyled>
                {isJobCodeLocked ? (
                  <FieldValueReadOnlyStyled>{val(record.businessUnit)}</FieldValueReadOnlyStyled>
                ) : (
                  <SbSelect options={businessUnits} value={record.businessUnit} placeholder="Select"
                    allowClear size="small" disabled={!record.legalEntity}
                    onChange={(v) => updateRow(record.key, { businessUnit: v, ...CASCADE_RESETS.businessUnit })} />
                )}
              </FieldItemStyled>
            )}
            {fields.function?.visible !== false && (
              <FieldItemStyled>
                <FieldLabelStyled>
                  {fields.function?.label || 'Function'}
                  {requiredKeys.has('functionDept') && <RequiredMark>*</RequiredMark>}
                </FieldLabelStyled>
                {isExcel && !excelFieldEditable('functionDept') ? (
                  <FieldValueReadOnlyStyled>{val(record.functionDept)}</FieldValueReadOnlyStyled>
                ) : isEditableExcel && !record.businessUnit ? (
                  <SbInput value={record.functionDept} placeholder="Enter function" size="small"
                    onValueChange={(v) => updateRow(record.key, { functionDept: v || null, ...CASCADE_RESETS.functionDept })} />
                ) : (
                  <SbSelect options={functions} value={record.functionDept} placeholder="Select"
                    allowClear showSearch size="small" disabled={!record.businessUnit}
                    onChange={(v) => updateRow(record.key, { functionDept: v, ...CASCADE_RESETS.functionDept })} />
                )}
              </FieldItemStyled>
            )}
          </FieldsRowStyled>

          {/* Field row 2 */}
          <FieldsRowStyled>
            {fields.subFunction?.visible !== false && (
              <FieldItemStyled>
                <FieldLabelStyled>
                  {fields.subFunction?.label || 'Sub-function'}
                  {requiredKeys.has('subFunction') && <RequiredMark>*</RequiredMark>}
                </FieldLabelStyled>
                {isExcel && !excelFieldEditable('subFunction') ? (
                  <FieldValueReadOnlyStyled>{val(record.subFunction)}</FieldValueReadOnlyStyled>
                ) : isEditableExcel && !record.functionDept ? (
                  <SbInput value={record.subFunction} placeholder="Enter sub-function" size="small"
                    onValueChange={(v) => updateRow(record.key, { subFunction: v || null, ...CASCADE_RESETS.subFunction })} />
                ) : (
                  <SbSelect options={subFunctions} value={record.subFunction} placeholder="Select"
                    allowClear showSearch size="small" disabled={!record.functionDept}
                    onChange={(v) => updateRow(record.key, { subFunction: v, ...CASCADE_RESETS.subFunction })} />
                )}
              </FieldItemStyled>
            )}
            {fields.specialty?.visible && (
              <FieldItemStyled>
                <FieldLabelStyled>
                  {fields.specialty?.label || 'Specialty'}
                  {requiredKeys.has('specialty') && hasRealSpecialties(specialties) && <RequiredMark>*</RequiredMark>}
                </FieldLabelStyled>
                {isExcel && !excelFieldEditable('specialty') ? (
                  <FieldValueReadOnlyStyled>{val(joinMulti(record.specialty))}</FieldValueReadOnlyStyled>
                ) : (
                  <SbSelect mode="multiple" options={specialties} value={record.specialty} placeholder="Select"
                    allowClear showSearch size="small" maxTagCount="responsive" disabled={!record.subFunction}
                    onChange={(v) => updateRow(record.key, { specialty: expandSentinels(v, specialties), invalidSpecialty: null })} />
                )}
              </FieldItemStyled>
            )}
            {fields.roleType?.visible !== false && (
              <FieldItemStyled>
                <FieldLabelStyled>{fields.roleType?.label || 'Role Type'}</FieldLabelStyled>
                {isJobCodeLocked ? (
                  <FieldValueReadOnlyStyled>{val(record.roleType)}</FieldValueReadOnlyStyled>
                ) : (
                  <SbSelect options={roleTypeOptions} value={record.roleType} placeholder="Select"
                    allowClear size="small"
                    onChange={(v) => updateRow(record.key, { roleType: v })} />
                )}
              </FieldItemStyled>
            )}
            {fields.level?.visible !== false && (
              <FieldItemStyled>
                <FieldLabelStyled>{fields.level?.label || 'Job Level'}</FieldLabelStyled>
                {isJobCodeLocked ? (
                  <FieldValueReadOnlyStyled>{jobLevelLabel}</FieldValueReadOnlyStyled>
                ) : (
                  <SbSelect options={jobLevelOptions} value={record.jobLevel} placeholder="Select"
                    allowClear size="small"
                    onChange={(v) => updateRow(record.key, { jobLevel: v })} />
                )}
              </FieldItemStyled>
            )}
            {fields.workEnvironment?.visible !== false && (
              <FieldItemStyled>
                <FieldLabelStyled>
                  {fields.workEnvironment?.label || 'Work Environment'}
                  {requiredKeys.has('workEnvironment') && <RequiredMark>*</RequiredMark>}
                </FieldLabelStyled>
                {isExcel && !excelFieldEditable('workEnvironment') ? (
                  <FieldValueReadOnlyStyled>{val(record.workEnvironment)}</FieldValueReadOnlyStyled>
                ) : (
                  <SbSelect options={workEnvironmentOptions} value={record.workEnvironment} placeholder="Select"
                    allowClear size="small"
                    onChange={(v) => updateRow(record.key, { workEnvironment: v, location: null })} />
                )}
              </FieldItemStyled>
            )}
            {fields.location?.visible !== false && (
              <FieldItemStyled>
                <FieldLabelStyled>
                  {fields.location?.label || 'Location'}
                  {requiredKeys.has('location') && <RequiredMark>*</RequiredMark>}
                </FieldLabelStyled>
                {isExcel && !excelFieldEditable('location') ? (
                  <FieldValueReadOnlyStyled>{val(record.location)}</FieldValueReadOnlyStyled>
                ) : isEditableExcel && !record.legalEntity ? (
                  <SbInput value={record.location} placeholder="Enter location" size="small"
                    onValueChange={(v) => updateRow(record.key, { location: v || null })} />
                ) : (
                  <SbSelect options={locations} value={record.location} placeholder="Select"
                    allowClear showSearch size="small" disabled={!record.legalEntity}
                    onChange={(v) => updateRow(record.key, { location: v })} />
                )}
              </FieldItemStyled>
            )}
            {fields.roleContext?.visible !== false && (
              <FieldItemStyled>
                <FieldLabelStyled>{fields.roleContext?.label || 'Role Context'}</FieldLabelStyled>
                {record.roleContext ? (
                  <RoleContextValueBoxStyled onClick={() => setRoleContextModal(record.roleContext)}>
                    <FieldValueStyled style={{ flex: 1, cursor: 'pointer' }}>
                      {record.roleContext}
                    </FieldValueStyled>
                    {!isExcel && (
                      <>
                        <SbButton buttonType="action" size="small" icon={<EditOutlined />}
                          onClick={(e) => { e.stopPropagation(); setRoleContextInput({ rowKey: record.key, value: record.roleContext }); }} />
                        <SbButton buttonType="action" size="small" icon={<CloseOutlined />}
                          onClick={(e) => { e.stopPropagation(); updateRow(record.key, { roleContext: null }); }} />
                      </>
                    )}
                  </RoleContextValueBoxStyled>
                ) : (
                  isExcel
                    ? <FieldValueReadOnlyStyled>—</FieldValueReadOnlyStyled>
                    : <SbButton buttonType="secondary" size="small"
                        onClick={() => setRoleContextInput({ rowKey: record.key, value: '' })}>
                        Add Role Context
                      </SbButton>
                )}
              </FieldItemStyled>
            )}
          </FieldsRowStyled>

          {/* Field row 3 */}
          <FieldsRowStyled>
            <FieldItemStyled>
              <FieldLabelStyled>Role Reporting To</FieldLabelStyled>
              <SbSelect options={roleReportingToOptions} value={record.roleReportingToSbUniqueId} placeholder="Select"
                allowClear showSearch size="small" disabled={record.isApexRole}
                onChange={(v) => updateRow(record.key, { roleReportingToSbUniqueId: v, isApexRole: v ? false : record.isApexRole })} />
            </FieldItemStyled>
            <FieldItemStyled style={{ justifySelf: 'start' }}>
              <FieldLabelStyled>Is Apex</FieldLabelStyled>
              <SbSwitch checked={!!record.isApexRole} disabled={!!record.roleReportingToSbUniqueId}
                onChange={(checked) => updateRow(record.key, { isApexRole: checked, roleReportingToSbUniqueId: checked ? null : record.roleReportingToSbUniqueId })} />
            </FieldItemStyled>
          </FieldsRowStyled>
        </RowCardBodyStyled>}
      </RowCardStyled>
    );
  };

  const handleGoToStep2 = () => {
    const invalidRows = rows.filter((r) => getMissingFields(r, requiredFields, orgStructure).length > 0);
    if (invalidRows.length > 0) {
      setInvalidatedKeys(new Set(invalidRows.map((r) => r.key)));
      setCollapsedKeys((prev) => {
        const next = new Set(prev);
        invalidRows.forEach((r) => next.delete(r.key));
        return next;
      });
      const excelInvalidKeys = invalidRows.filter((r) => r.source === 'excel').map((r) => r.key);
      if (excelInvalidKeys.length > 0) {
        setEditableKeys((prev) => new Set([...prev, ...excelInvalidKeys]));
      }
      pendingScrollKey.current = invalidRows[0].key;
      return;
    }
    setStep(2);
  };

  const handleBackToStep1 = () => {
    if (!hasZip && !mappingsConfirmed) {
      setStep(1);
      return;
    }
    showSbConfirm({
      title: 'Go back to Step 1?',
      content: 'Going back will discard your uploaded ZIP file and all mapping decisions. Your row data will be kept.',
      okText: 'Yes, Go Back',
      cancelText: 'Stay on Step 2',
      onOk: () => {
        handleZipRemove();
        setStep(1);
      },
    });
  };

  const stepBar = (
    <StepBarStyled>
      <StepBarLeftStyled>
        <StepClickableGroupStyled onClick={handleBackToStep1}>
          <StepBadgeStyled $active={step === 1}>1</StepBadgeStyled>
          <StepLabelStyled $active={step === 1}>
            Review Rows
            {step === 1 && (
              <StepHintStyled>— Download template, fill in details &amp; Upload the Excel</StepHintStyled>
            )}
          </StepLabelStyled>
        </StepClickableGroupStyled>
        <StepDividerStyled>›</StepDividerStyled>
        <StepClickableGroupStyled onClick={() => rows.length > 0 && handleGoToStep2()}>
          <StepBadgeStyled $active={step === 2}>2</StepBadgeStyled>
          <StepLabelStyled $active={step === 2}>
            Attach Reference
            {step === 2 && (
              <StepHintStyled>— upload a ZIP containing all reference files or upload individually</StepHintStyled>
            )}
          </StepLabelStyled>
        </StepClickableGroupStyled>
      </StepBarLeftStyled>

      {step === 1 && (
        <StepBarRightStyled>
            <SbTooltip content={TOOLTIP_DOWNLOAD_TEMPLATE}>
              <PremiumButtonWrapper>
                <SbButton buttonType="secondary" size="small" icon={<DownloadOutlined />} onClick={handleDownloadTemplate} loading={downloadingTemplate}>
                  Download Template
                </SbButton>
              </PremiumButtonWrapper>
            </SbTooltip>
            <SbTooltip content={TOOLTIP_DROP_ZONE}>
              <span>
                <SbUpload
                  accept=".xls,.xlsx"
                  maxCount={1}
                  showUploadList={false}
                  disabled={fileList.length > 0}
                  beforeUpload={async (file) => {
                    const error = await validateExcelFile(file);
                    if (error) {
                      dispatch(showToast(error, 'error'));
                      return false;
                    }
                    setFileList([file]);
                    parseExcelFile(file);
                    return false;
                  }}
                >
                  <PremiumButtonWrapper>
                    <SbButton buttonType="primary" size="small" icon={<UploadOutlined />} disabled={fileList.length > 0}>
                      Upload Excel
                    </SbButton>
                  </PremiumButtonWrapper>
                </SbUpload>
              </span>
            </SbTooltip>
            {fileList.length > 0 && (
              <SbFilePill
                fileName={fileList[0].name}
                onPreview={() => previewFile(fileList[0])}
                onRemove={mappingsConfirmed ? undefined : handleDiscardFile}
              />
            )}
        </StepBarRightStyled>
      )}

      {step === 2 && (
        <StepBarRightStyled>
          <FolderOpenOutlined style={{ color: '#6B7280', fontSize: 14 }} />
          {!hasZip ? (
            mappingsConfirmed ? (
              <span style={{ fontSize: 12, color: '#6B7280' }}>No reference ZIP — all JDs will be generated from metadata.</span>
            ) : (
              <>
                <SbUpload accept=".zip" maxCount={1} showUploadList={false}
                  beforeUpload={(file) => { handleZipUpload(file); return false; }}>
                  <SbButton buttonType="secondary" size="small" icon={<UploadOutlined />}>
                    Upload Zip
                  </SbButton>
                </SbUpload>
                <span style={{ fontSize: 12, color: '#6B7280' }}>
                  Name files after their job title for accurate mapping — e.g. <em>Backend_Engineer.pdf</em> or <em>SVP_Marketing.docx</em>
                </span>
              </>
            )
          ) : (
            <>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 10px', borderRadius: 4, border: '1px solid #E5E7EB',
                background: '#F3F4F6', fontSize: 12, fontWeight: 500, color: '#111827',
                maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {zipFilename}
              </span>
              {!mappingsConfirmed && (
                <SbButton buttonType="action" size="small" icon={<CloseOutlined />} onClick={handleZipRemoveWithConfirm} />
              )}
            </>
          )}
          {zipError && <span style={{ fontSize: 12, color: '#DC2626' }}>{zipError}</span>}
        </StepBarRightStyled>
      )}
    </StepBarStyled>
  );

  const body = (
    <>
      {stepBar}

      {step === 1 && (
        <>
          <RowListStyled>
            {rows.length === 0 ? (
              <EmptyStateStyled>No rows yet — upload an Excel file or click Add Row.</EmptyStateStyled>
            ) : (
              rows.map((record, idx) => renderRowCard(record, idx))
            )}
          </RowListStyled>

          <SubmitRowStyled>
            <ToolbarActionsStyled>
              <SbButton
                buttonType="secondary"
                icon={<PlusOutlined />}
                onClick={addRow}
                disabled={rows.length >= BATCH_LIMIT || submitting}
              >
                Add Row
              </SbButton>
              <SbButton
                buttonType="secondary"
                onClick={handleRemoveAll}
                disabled={rows.length === 0 || submitting || mappingsConfirmed}
              >
                Remove All
              </SbButton>
              {rows.length > 0 && (
                <>
                  <SbButton buttonType="secondary" onClick={collapseAll} disabled={collapsedKeys.size === rows.length || submitting}>
                    Collapse All
                  </SbButton>
                  <SbButton buttonType="secondary" onClick={expandAll} disabled={collapsedKeys.size === 0 || submitting}>
                    Expand All
                  </SbButton>
                </>
              )}
            </ToolbarActionsStyled>
            <ToolbarActionsStyled>
              {rows.length > 0 && (
                <Text type="secondary">
                  {rows.length} / {BATCH_LIMIT} row{rows.length !== 1 ? 's' : ''}
                  {rows.length > BATCH_LIMIT && (
                    <Text type="warning" style={{ marginLeft: 4 }}>
                      — remove excess, only {BATCH_LIMIT} can be processed!
                    </Text>
                  )}
                </Text>
              )}
              <SbButton
                buttonType="primary"
                disabled={rows.length === 0 || rows.length > BATCH_LIMIT}
                onClick={handleGoToStep2}
              >
                Next: Attach a zip containing Reference Files →
              </SbButton>
            </ToolbarActionsStyled>
          </SubmitRowStyled>
        </>
      )}

      {step === 2 && (
        <>
          <SbZipMappingReview
            ref={reviewRef}
            rows={rows}
            hasZip={hasZip}
            zipError={zipError}
            autoMapped={zipMatches?.autoMapped ?? []}
            needsReview={zipMatches?.needsReview ?? []}
            onZipUpload={handleZipUpload}
            onZipRemove={handleZipRemoveWithConfirm}
            onConfirm={handleConfirmMappings}
            zipFilename={zipFilename}
            rowIndexMap={Object.fromEntries(rows.map((r, i) => [r.key, i]))}
            locked={mappingsConfirmed}
            onPendingCountChange={setZipPendingCount}
          />

          <SubmitRowStyled>
            <ToolbarActionsStyled>
              <SbButton buttonType="secondary" onClick={handleBackToStep1}>
                ← Back to Step 1
              </SbButton>
              <SbButton
                buttonType="secondary"
                disabled={!hasZip && !mappingsConfirmed}
                onClick={() => showSbConfirm({
                  title: 'Discard all mappings?',
                  content: hasZip
                    ? 'This will remove the uploaded ZIP and all mapping decisions. Rows will remain but files will be cleared.'
                    : 'This will reset the confirmation. You can then upload a ZIP or re-confirm to generate all JDs from metadata.',
                  okText: 'Yes, Discard',
                  cancelText: 'No',
                  onOk: handleZipRemove,
                })}
              >
                Discard All Mappings
              </SbButton>
            </ToolbarActionsStyled>
            <ToolbarActionsStyled>
              <SbButton
                buttonType="secondary"
                disabled={mappingsConfirmed}
                onClick={() => {
                  const decisions = reviewRef.current?.buildDecisions() ?? {};
                  if (hasZip && zipPendingCount > 0) {
                    setConfirmToaster({ decisions });
                  } else {
                    handleConfirmMappings(decisions);
                  }
                }}
              >
                {mappingsConfirmed
                  ? hasZip ? '✓ Mappings Confirmed' : '✓ Generate from Metadata'
                  : hasZip
                    ? zipPendingCount > 0
                      ? `Skip & Confirm (${zipPendingCount} unresolved)`
                      : 'Confirm Mapping'
                    : 'Generate from Metadata'}
              </SbButton>
              <SbTooltip content={TOOLTIP_SUBMIT_BATCH}>
                <span>
                  <SbButton
                    buttonType="primary"
                    disabled={rows.length === 0 || rows.length > BATCH_LIMIT || !mappingsConfirmed}
                    onClick={handleSubmitAll}
                  >
                    Submit All
                  </SbButton>
                </span>
              </SbTooltip>
            </ToolbarActionsStyled>
          </SubmitRowStyled>
        </>
      )}

      <SbInfoToaster
        show={!!confirmToaster}
        title={hasZip ? 'Confirm file mappings' : 'Generate from metadata'}
        message={
          hasZip
            ? `${zipPendingCount > 0 ? `${zipPendingCount} row${zipPendingCount !== 1 ? 's' : ''} ${zipPendingCount !== 1 ? 'have' : 'has'} no file selected — ` : ''}JDs will be generated from metadata for unmatched rows. Are you okay with this?`
            : `All ${rows.length} JD${rows.length !== 1 ? 's' : ''} will be generated from metadata as no reference ZIP was uploaded. Are you okay with this?`
        }
        action="Proceed"
        viewCancel
        onClose={() => setConfirmToaster(null)}
        actionFunction={async () => {
          const { decisions } = confirmToaster;
          setConfirmToaster(null);
          await handleConfirmMappings(decisions);
        }}
      />

      <SbModal
        open={!!roleContextModal}
        title="Role Context"
        onCancel={() => setRoleContextModal(null)}
        footer={null}
      >
        <Text>{roleContextModal}</Text>
      </SbModal>

      <SbModal
        open={!!roleContextInput}
        title={roleContextInput?.value ? 'Edit Role Context' : 'Add Role Context'}
        onCancel={() => setRoleContextInput(null)}
        okText="Save"
        onOk={() => {
          const text = roleContextInput?.value?.trim();
          if (text) updateRow(roleContextInput.rowKey, { roleContext: text });
          setRoleContextInput(null);
        }}
      >
        <SbInput
          textarea
          rows={6}
          placeholder="Enter role context text…"
          value={roleContextInput?.value || ''}
          onValueChange={(v) => setRoleContextInput(prev => ({ ...prev, value: v }))}
        />
      </SbModal>
    </>
  );

  if (embedded) return <BulkEmbedContainerStyled>{body}</BulkEmbedContainerStyled>;

  return (
    <PageContainerStyled>
      <PageHeaderStyled>
        <HeaderLeftStyled>
          <HeadingRowStyled>
            <Title level={3}>Bulk JD Creation</Title>
          </HeadingRowStyled>
          <Text type="secondary">
            Upload an Excel file or add rows manually, attach JDs per row, then submit.
          </Text>
        </HeaderLeftStyled>
        <HeaderRightStyled>
          <SbButton
            buttonType="action"
            size="small"
            icon={<CloseOutlined />}
            onClick={() => navigate("/profile", { replace: true })}
            title="Back to Manage Profiles"
          />
        </HeaderRightStyled>
      </PageHeaderStyled>
      {body}
    </PageContainerStyled>
  );
};

export default SbBulkJdUpload;
